/**
 * CustomSelect — progressive enhancement for `<select data-custom-select>`.
 *
 * Native selects cannot style their popup list, so the element is kept in the DOM
 * (visually hidden) as the source of truth and a styled trigger + listbox is built
 * beside it. Picking an option writes back to the native select and fires a
 * `change` event, so existing form code that reads `$(sel).val()` keeps working.
 *
 * Styles live in `assets/app.scss` (`.custom-select*`), not in Tailwind utilities,
 * because Tailwind does not scan `assets/`.
 */
(function () {
  // arrow_drop_down/Sharp, exported from the Figma design system.
  // The open state is the same glyph rotated 180deg (identical to arrow_drop_up).
  var ARROW_SVG =
    '<svg width="10" height="5" viewBox="0 0 10 5" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true" focusable="false">' +
    '<path d="M0 0L5 5L10 0H0Z" fill="currentColor"/></svg>';

  var uid = 0;

  function CustomSelect(select) {
    this.select = select;
    this.uid = 'custom-select-' + ++uid;
    this.isOpen = false;
    this.activeIndex = -1;
    this.typeahead = '';
    this.typeaheadTimer = null;
    // Published so callers that change the native options in place (disabling
    // them, say) can re-sync the styled list without firing a `change` event.
    select.customSelect = this;

    this._build();
    this._bind();
    this.sync();
  }

  CustomSelect.prototype._build = function () {
    var select = this.select;

    this.root = document.createElement('div');
    // Layout classes authored on the <select> belong to the field as a whole —
    // move them to the wrapper, since the native element is taken out of flow.
    this.root.className = ('custom-select ' + select.className).trim();

    select.parentNode.insertBefore(this.root, select);
    this.root.appendChild(select);
    select.className = 'custom-select__native';
    select.setAttribute('tabindex', '-1');

    this.trigger = document.createElement('button');
    this.trigger.type = 'button';
    this.trigger.className = 'custom-select__trigger';
    this.trigger.id = this.uid + '-trigger';
    this.trigger.setAttribute('aria-haspopup', 'listbox');
    this.trigger.setAttribute('aria-expanded', 'false');

    this.value = document.createElement('span');
    this.value.className = 'custom-select__value';
    this.trigger.appendChild(this.value);

    var icon = document.createElement('span');
    icon.className = 'custom-select__icon';
    icon.innerHTML = ARROW_SVG;
    this.trigger.appendChild(icon);

    this.list = document.createElement('ul');
    this.list.className = 'custom-select__list';
    this.list.id = this.uid + '-list';
    this.list.setAttribute('role', 'listbox');
    this.list.hidden = true;

    this.trigger.setAttribute('aria-controls', this.list.id);

    this.root.appendChild(this.trigger);
    this.root.appendChild(this.list);

    // Point the field's <label for="…"> at the trigger so clicking it opens the dropdown.
    var label = select.id
      ? document.querySelector('label[for="' + select.id + '"]')
      : null;
    if (label) {
      label.setAttribute('for', this.trigger.id);
      if (!label.id) label.id = this.uid + '-label';
      this.trigger.setAttribute('aria-labelledby', label.id);
      this.list.setAttribute('aria-labelledby', label.id);
    }
  };

  CustomSelect.prototype._bind = function () {
    var self = this;

    this.trigger.addEventListener('click', function () {
      self.toggle();
    });

    this.trigger.addEventListener('keydown', function (e) {
      self._onTriggerKeydown(e);
    });

    this.list.addEventListener('click', function (e) {
      var option = e.target.closest('.custom-select__option');
      if (!option) return;
      self.commit(self.options.indexOf(option));
    });

    this.list.addEventListener('mousemove', function (e) {
      var option = e.target.closest('.custom-select__option');
      if (option) self._setActive(self.options.indexOf(option), false);
    });

    // Native select changed from elsewhere (e.g. jQuery `.val(…).trigger('change')`).
    // Bind through jQuery when it is present: a jQuery-triggered `change` never
    // reaches a plain addEventListener handler, but a native one reaches both.
    if (window.jQuery) {
      window.jQuery(this.select).on('change', function () {
        self.sync();
      });
    } else {
      this.select.addEventListener('change', function () {
        self.sync();
      });
    }

    // Focus forwarded from the hidden native element (label clicks, validation focus).
    this.select.addEventListener('focus', function () {
      self.trigger.focus();
    });

    document.addEventListener('click', function (e) {
      if (self.isOpen && !self.root.contains(e.target)) self.close();
    });
  };

  /**
   * Mirror the native select into the custom UI. The option list is only rebuilt
   * when the native option count changes — the time zone select carries ~400
   * options and rebuilding it on every selection would be needless DOM work.
   */
  CustomSelect.prototype.sync = function () {
    var native = this.select.options;
    var selectedIndex = this.select.selectedIndex;

    if (!this.options || this.options.length !== native.length) {
      this._renderOptions();
    }

    // aria-selected *and* aria-disabled are mirrored on every sync, not just when
    // the list is rebuilt: an option's `disabled` can be toggled in place after
    // render (the gift schedule greys out times that have already passed).
    this.options.forEach(function (option, index) {
      option.setAttribute(
        'aria-selected',
        index === selectedIndex ? 'true' : 'false',
      );
      if (native[index].disabled) option.setAttribute('aria-disabled', 'true');
      else option.removeAttribute('aria-disabled');
    });

    this.value.textContent =
      selectedIndex > -1 ? native[selectedIndex].text : '';
    this._setActive(selectedIndex, false);
  };

  /**
   * `this.options` stays index-aligned with the native `select.options` — every
   * keyboard and click path relies on that — so `<optgroup>` labels are rendered
   * as extra, non-option rows slipped in ahead of their first option rather
   * than as entries of their own.
   */
  CustomSelect.prototype._renderOptions = function () {
    const self = this;
    const fragment = document.createDocumentFragment();
    let lastGroup = null;

    this.list.innerHTML = '';
    this.options = Array.prototype.map.call(
      this.select.options,
      function (nativeOption, index) {
        const group =
          nativeOption.parentElement instanceof HTMLOptGroupElement
            ? nativeOption.parentElement
            : null;
        if (group && group !== lastGroup) {
          const heading = document.createElement('li');
          heading.className = 'custom-select__group';
          heading.setAttribute('role', 'presentation');
          heading.textContent = group.label;
          fragment.appendChild(heading);
        }
        lastGroup = group;

        const option = document.createElement('li');
        option.className = 'custom-select__option';
        if (group) option.classList.add('custom-select__option--grouped');
        option.id = self.uid + '-option-' + index;
        option.setAttribute('role', 'option');
        option.textContent = nativeOption.text;
        fragment.appendChild(option);
        return option;
      },
    );
    this.list.appendChild(fragment);
  };

  CustomSelect.prototype.toggle = function () {
    if (this.isOpen) this.close();
    else this.open();
  };

  CustomSelect.prototype.open = function () {
    if (this.isOpen) return;
    this.isOpen = true;
    this.list.hidden = false;
    this.trigger.setAttribute('aria-expanded', 'true');
    this._positionList();
    this._setActive(this.select.selectedIndex, true);
  };

  CustomSelect.prototype.close = function () {
    if (!this.isOpen) return;
    this.isOpen = false;
    this.list.hidden = true;
    this.trigger.setAttribute('aria-expanded', 'false');
    this.trigger.removeAttribute('aria-activedescendant');
    this.root.classList.remove('custom-select--above');
  };

  /**
   * Flip the list above the trigger when there is not enough room below —
   * unless the select carries `data-custom-select-drop="down"`. A select
   * inside an `overflow-hidden` card (the PDP's eligible-experiences filters)
   * has its flipped list clipped at the card's top edge; there the list stays
   * below, where the card always has room, and the page is scrolled just far
   * enough to show it.
   */
  CustomSelect.prototype._positionList = function () {
    this.root.classList.remove('custom-select--above');
    if (this.select.dataset.customSelectDrop === 'down') {
      this.list.scrollIntoView({ block: 'nearest', behavior: 'smooth' });
      return;
    }
    const triggerRect = this.trigger.getBoundingClientRect();
    const listHeight = this.list.offsetHeight;
    const spaceBelow = window.innerHeight - triggerRect.bottom;
    if (spaceBelow < listHeight + 8 && triggerRect.top > spaceBelow) {
      this.root.classList.add('custom-select--above');
    }
  };

  CustomSelect.prototype._setActive = function (index, scroll) {
    if (!this.options || !this.options.length) return;
    index = Math.max(0, Math.min(index, this.options.length - 1));

    this.options.forEach(function (option, i) {
      option.classList.toggle('is-active', i === index);
    });
    this.activeIndex = index;

    if (this.isOpen) {
      this.trigger.setAttribute(
        'aria-activedescendant',
        this.options[index].id,
      );
      if (scroll) this.options[index].scrollIntoView({ block: 'nearest' });
    }
  };

  /** Write the choice back to the native select and notify listeners. */
  CustomSelect.prototype.commit = function (index) {
    var nativeOption = this.select.options[index];
    if (!nativeOption || nativeOption.disabled) return;

    if (this.select.selectedIndex !== index) {
      this.select.selectedIndex = index;
      this.select.dispatchEvent(new Event('change', { bubbles: true }));
    } else {
      this.sync();
    }

    this.close();
    this.trigger.focus();
  };

  CustomSelect.prototype._onTriggerKeydown = function (e) {
    var key = e.key;
    var last = this.options.length - 1;

    if (key === 'Escape') {
      if (this.isOpen) {
        e.preventDefault();
        this.close();
      }
      return;
    }

    if (key === 'Enter' || key === ' ' || key === 'Spacebar') {
      e.preventDefault();
      if (this.isOpen) this.commit(this.activeIndex);
      else this.open();
      return;
    }

    if (key === 'ArrowDown' || key === 'ArrowUp') {
      e.preventDefault();
      if (!this.isOpen) {
        this.open();
        return;
      }
      this._setActive(this.activeIndex + (key === 'ArrowDown' ? 1 : -1), true);
      return;
    }

    if (key === 'Home' || key === 'End') {
      e.preventDefault();
      if (!this.isOpen) this.open();
      this._setActive(key === 'Home' ? 0 : last, true);
      return;
    }

    if (key === 'Tab') {
      this.close();
      return;
    }

    if (key.length === 1 && !e.metaKey && !e.ctrlKey && !e.altKey) {
      this._onTypeahead(key);
    }
  };

  /** Jump to the first option matching the recently typed characters. */
  CustomSelect.prototype._onTypeahead = function (char) {
    var self = this;
    clearTimeout(this.typeaheadTimer);
    this.typeahead += char.toLowerCase();
    this.typeaheadTimer = setTimeout(function () {
      self.typeahead = '';
    }, 500);

    var query = this.typeahead;
    var match = -1;
    for (var i = 0; i < this.options.length; i++) {
      // Start searching after the active option so repeated letters cycle forward.
      var index = (this.activeIndex + 1 + i) % this.options.length;
      if (this.options[index].textContent.toLowerCase().indexOf(query) === 0) {
        match = index;
        break;
      }
    }
    if (match === -1) return;

    if (this.isOpen) this._setActive(match, true);
    else this.commit(match);
  };

  function init(root) {
    var scope = root || document;
    var selects = scope.querySelectorAll('select[data-custom-select]');
    Array.prototype.forEach.call(selects, function (select) {
      if (select.closest('.custom-select')) return;
      new CustomSelect(select);
    });
  }

  window.CustomSelect = { init: init };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () {
      init();
    });
  } else {
    init();
  }
})();
